import clsx from 'clsx';
import { m, LazyMotion, domAnimation, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Line from './images/line.inline.svg';
import QuoteIcon from './images/quote.inline.svg';
import Item, { CONTENT_TYPES } from './item';

import 'swiper/css';

const AUTOPLAY_DURATION_IN_MS = 6000;

const Testimonials = ({ items, type }) => {
  const sliderRef = useRef();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  return (
    <section
      className={clsx(
        'safe-paddings overflow-hidden bg-gray-1',
        type === 'company-logo' && 'py-60 xl:py-40 lg:py-20 md:py-14',
        type === 'author-photo' && 'py-40 xl:py-30 lg:py-20 md:py-14'
      )}
    >
      <div
        className={clsx(
          type === 'company-logo' && 'container-sm',
          type === 'author-photo' && 'container'
        )}
      >
        <div className="relative flex items-start lg:block">
          <QuoteIcon className="relative z-10 w-10 shrink-0" />

          {items.length > 1 ? (
            <Swiper
              className="!ml-6 lg:mt-8 lg:!ml-0 md:mt-6"
              slidesPerView={1}
              modules={[Autoplay]}
              ref={sliderRef}
              autoplay={{ delay: AUTOPLAY_DURATION_IN_MS, disableOnInteraction: false }}
              breakpoints={{
                768: {
                  autoHeight: false,
                },
              }}
              loop
              autoHeight
              onSlideChange={(swiper) => setActiveSlideIndex(swiper.realIndex)}
            >
              {items.map((data, index) => (
                <SwiperSlide key={index}>
                  <Item data={data} type={type} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="!ml-6 lg:mt-8 lg:!ml-0 md:mt-6">
              <Item data={items[0]} type={type} />
            </div>
          )}

          {items.length > 1 && (
            <div
              className={clsx(
                'absolute right-0 z-10 flex space-x-3 bg-gray-1 lg:top-2 lg:bottom-auto',
                type === 'company-logo' && 'bottom-[7px]',
                type === 'author-photo' && 'bottom-[30px]'
              )}
            >
              {items.map((_, index) => (
                <button
                  className="relative h-3 w-3 rounded-full bg-gray-5"
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  key={index}
                  onClick={() => sliderRef?.current?.swiper?.slideTo(index + 1)}
                >
                  <LazyMotion features={domAnimation}>
                    <AnimatePresence>
                      {activeSlideIndex === index && (
                        <m.svg
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-1"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1, transition: { duration: 0.2 } }}
                          exit={{ opacity: 0, transition: { duration: 0.2 } }}
                        >
                          <m.path
                            d="M8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14Z"
                            stroke="#f25a5a"
                            strokeWidth="4"
                            initial={{ pathLength: 0 }}
                            animate={{
                              pathLength: 1,
                              transition: { duration: AUTOPLAY_DURATION_IN_MS / 1000 },
                            }}
                          />
                        </m.svg>
                      )}
                    </AnimatePresence>
                  </LazyMotion>
                </button>
              ))}
            </div>
          )}

          <div
            className="absolute top-[-60px] left-[-128px] h-[280px] w-[280px] rounded-full bg-primary-1 xl:top-[-50px] xl:left-[-100px] xl:h-[240px] xl:w-[240px] lg:hidden"
            aria-hidden
          />
          <Line
            className="absolute top-[60px] left-24 w-[852px] -translate-x-full 2xl:left-36 xl:left-40 lg:hidden"
            aria-hidden
          />
        </div>
      </div>
    </section>
  );
};

Testimonials.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      author: PropTypes.string,
      logoUrl: PropTypes.string,
      logo: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      authorPhoto: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      authorName: PropTypes.string,
      authorPosition: PropTypes.string,
      readMoreUrl: PropTypes.string,
    })
  ).isRequired,
  type: PropTypes.oneOf(Object.keys(CONTENT_TYPES)).isRequired,
};

export default Testimonials;
